import { useCurrencyContext } from '@contexts/currency-context';
import type { Checkin, Rate, Room } from '@interfaces/expedia/property';
import { formatCurrencyV2 } from '@utils/format-currency-v2';
import { Bed, CornerUpLeft, Typography, Users01 } from '@v2/ui';
import Image from 'next/image';
import { useMemo } from 'react';
import { OccupancyRates } from './OccupancyRates';
import { RoomDetailsDialog } from './RoomsDetailsDialog';

type RoomCardProps = {
  room: Room;
  checkin?: Checkin;
  rate: Rate;
  minimumPricePerOccupancy?: Record<string, number>;
  onSelectRoom: (
    room: Room,
    rate: Rate,
    ratePrice: number,
    occupancy,
    modifier: number
  ) => void;
};

export function RoomCard({
  room,
  checkin,
  rate,
  minimumPricePerOccupancy,
  onSelectRoom,
}: RoomCardProps) {
  const { occupancyPricing, refundable } = rate;

  const { description } = rate.bedgroups[0];
  const { priceRate, currency } = useCurrencyContext();

  const roomPrice = useMemo(() => {
    const price = occupancyPricing.map((pricing) =>
      (
        +pricing.total.value -
        (minimumPricePerOccupancy?.[pricing.occupancy] ?? 0)
      ).toFixed(2)
    )[0];

    return formatCurrencyV2(price, priceRate, currency);
  }, [occupancyPricing, minimumPricePerOccupancy, priceRate, currency]);

  /*
   * 1 - Adds new room
   * -1 - removes  room
   */

  const handleSelectRoom = (
    price: number,
    occupancy: string,
    modifier: number
  ) => {
    onSelectRoom(room, rate, price, occupancy, modifier);
  };

  return (
    <div className="flex min-h-[520px] w-full max-w-[400px] flex-col rounded-lg border border-gray-200 md:h-[604px]">
      <div className="relative h-[200px] min-h-[200px] w-full">
        <Image
          src={room.images[0]}
          className="rounded-t-lg object-cover"
          alt="Room photo"
          fill={true}
        />
      </div>

      <div className="flex h-full flex-col justify-between rounded-b-lg bg-white p-4 sm:p-6">
        <div>
          <Typography size="tlg" className="mb-4 font-bold">
            {room.name}
            {/* {description} */}
          </Typography>
          <ul className="space-y-2.5">
            <li className="flex items-center gap-x-2.5">
              <Bed className="shrink-0 text-gray-500" />
              <span className="text-base font-medium">{description}</span>
            </li>
            <li className="flex items-center gap-x-2.5">
              <Users01 size="16" className="shrink-0 text-gray-500" />
              <span className="text-base font-medium">
                Sleeps {room.capacity.total}
              </span>
            </li>
            <li className="flex items-center gap-x-2.5">
              <CornerUpLeft size="16" className="shrink-0 text-gray-500" />
              <span className="text-base font-medium">
                {refundable ? 'Refundable' : 'Non-refundable'}
              </span>
              {/* <span>{rate.configuration.map(rate => )}</span> */}
            </li>

            <li className="pt-0.5">
              <RoomDetailsDialog
                room={room}
                checkin={checkin}
                rate={rate}
                price={roomPrice}
                handleSelectRoom={handleSelectRoom}
                // @ts-ignore temp
                minimumPricePerOccupancy={minimumPricePerOccupancy}
              />
            </li>
          </ul>
        </div>

        <div className="mt-4 flex items-end justify-between gap-2 pt-2 sm:mt-6 sm:pt-0">
          <div className="min-w-0">
            <h4 className="whitespace-nowrap text-2xl font-bold sm:text-3xl">
              + {roomPrice}
            </h4>
            <span className="mt-1 block text-xs font-medium text-gray-500 sm:text-sm">
              Per room for the stay <br /> Includes taxes & fees
            </span>
          </div>
          <div className="flex shrink-0 flex-col items-end space-y-2.5 sm:space-y-3">
            <span className="whitespace-nowrap text-xs font-medium text-gray-500 sm:text-sm">
              {rate.availableRooms} available
            </span>

            <OccupancyRates
              rateId={rate.id}
              // @ts-ignore temp
              minimumPricePerOccupancy={minimumPricePerOccupancy}
              occupancyRates={occupancyPricing}
              onSelect={handleSelectRoom}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
